/**
 * Error handling utilities for standardized error management
 */

interface AppErrorConfig {
  code: string;
  message: string;
  details?: Record<string, string | number | boolean>;
  httpStatus?: number;
}

interface ErrorResponse {
  success: false;
  error: string;
  errorCode?: string;
  errorDetails?: Record<string, string | number | boolean>;
}

/**
 * Standardized service result interface
 * Use this for all async service functions to ensure consistent error handling
 */
export interface ServiceResult<T> {
  success: boolean;
  data: T | null;
  error: string | null;
}

/**
 * Type guard to check if a ServiceResult is successful
 */
export function isServiceSuccess<T>(result: ServiceResult<T>): result is ServiceResult<T> & { success: true; data: T; error: null } {
  return result.success && result.data !== null;
}

/**
 * Type guard to check if a ServiceResult is a failure
 */
export function isServiceError<T>(result: ServiceResult<T>): result is ServiceResult<T> & { success: false; data: null; error: string } {
  return !result.success;
}

/**
 * Create a successful service result
 */
export function serviceSuccess<T>(data: T): ServiceResult<T> {
  return {
    success: true,
    data,
    error: null,
  };
}

/**
 * Create a failed service result
 * Accepts unknown to safely handle any error from catch blocks
 */
// eslint-disable-next-line no-restricted-syntax -- unknown is required for catch block error handling
export function serviceError<T>(error: unknown, context?: string): ServiceResult<T> {
  const errorMessage = getErrorMessage(error);
  const fullMessage = context ? `${context}: ${errorMessage}` : errorMessage;

  console.error(fullMessage, error);

  return {
    success: false,
    data: null,
    error: fullMessage,
  };
}

/**
 * Extract error message from unknown error type
 * Accepts unknown to safely handle any error from catch blocks
 */
// eslint-disable-next-line no-restricted-syntax -- unknown is required for catch block error handling
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    // eslint-disable-next-line no-restricted-syntax -- unknown is needed for safe type narrowing
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string') {
      return msg;
    }
  }
  return 'Unknown error';
}


/**
 * Types of application errors
 */
export const ErrorTypes = {
  // Authentication errors
  AUTH_REQUIRED: "auth/authentication-required",
  INVALID_CREDENTIALS: "auth/invalid-credentials",
  USER_NOT_FOUND: "auth/user-not-found",
  EMAIL_IN_USE: "auth/email-in-use",
  WEAK_PASSWORD: "auth/weak-password",

  // Database errors
  DB_CONNECTION: "database/connection-error",
  DB_QUERY: "database/query-error",
  DB_CONSTRAINT: "database/constraint-violation",

  // Validation errors
  VALIDATION: "validation/invalid-input",
  REQUIRED_FIELD: "validation/required-field",
  INVALID_FORMAT: "validation/invalid-format",

  // File errors
  FILE_UPLOAD: "file/upload-error",
  FILE_DELETE: "file/delete-error",
  FILE_LIMIT: "file/size-limit-exceeded",

  // General errors
  NOT_FOUND: "general/not-found",
  FORBIDDEN: "general/forbidden",
  SERVER_ERROR: "general/server-error",

  // Rate limiting
  RATE_LIMIT: "limit/rate-limit-exceeded",
  PLAN_LIMIT: "limit/plan-limit-exceeded",

  // Specific feature errors
  COLLECTION_ACCESS: "collection/access-denied",
  MEDIA_PROCESS: "media/processing-error",
};

/**
 * Creates a standardized app error
 * @param code - Error code
 * @param message - Error message
 * @param details - Additional error details
 * @param httpStatus - HTTP status code
 * @returns App error object
 */
export function createAppError(
  code: string,
  message: string,
  details?: Record<string, string | number | boolean>,
  httpStatus = 400
): AppErrorConfig {
  return {
    code,
    message,
    details,
    httpStatus,
  };
}

// PostgreSQL error code to message mapping
const PG_ERROR_MESSAGES: Record<string, string> = {
  23503: 'Related record not found',
  23505: 'Record already exists',
  23514: 'Invalid data for database constraints',
};

/**
 * Maps database errors to application errors
 */
export function mapDbError(error: Error): AppErrorConfig {
  // Handle PostgreSQL/Supabase errors with error codes
  const pgError = error as Error & { code?: string; detail?: Record<string, string | number | boolean> };

  if (pgError.code && PG_ERROR_MESSAGES[pgError.code]) {
    return createAppError(
      ErrorTypes.DB_CONSTRAINT,
      PG_ERROR_MESSAGES[pgError.code],
      pgError.detail
    );
  }

  return createAppError(
    ErrorTypes.DB_QUERY,
    error.message || 'Database error occurred',
    { message: error.message, name: error.name }
  );
}

/**
 * Maps auth errors to application errors
 * @param error - Auth error
 * @returns App error object
 */
export function mapAuthError(error: Error): AppErrorConfig {
  const message = error.message;

  if (message.includes("Invalid login credentials")) {
    return createAppError(
      ErrorTypes.INVALID_CREDENTIALS,
      "Invalid email or password"
    );
  }

  if (message.includes("User already registered")) {
    return createAppError(ErrorTypes.EMAIL_IN_USE, "Email already in use");
  }

  if (message.includes("Signup disabled")) {
    return createAppError(
      ErrorTypes.AUTH_REQUIRED,
      "Signup is currently disabled"
    );
  }

  return createAppError(
    ErrorTypes.AUTH_REQUIRED,
    message || "Authentication error occurred",
    { message: error.message, name: error.name }
  );
}

/**
 * Creates a standardized error response
 */
export function createErrorResponse(error: Error | string): ErrorResponse {
  if (typeof error === 'string') {
    return { success: false, error };
  }

  const appError = error as Error & { code?: string; details?: Record<string, string | number | boolean> };

  return {
    success: false,
    error: appError.message || 'An unexpected error occurred',
    ...(appError.code && { errorCode: appError.code }),
    ...(appError.details && { errorDetails: appError.details }),
  };
}

/**
 * General error handler for forms and async operations
 * @param error - Error to handle
 * @param options - Options for handling the error
 * @returns Object with error message
 */
export function handleError(
  error: Error,
  options?: {
    logError?: boolean;
    defaultMessage?: string;
  }
): { message: string } {
  const { logError = true, defaultMessage = "An unexpected error occurred" } =
    options ?? {};

  if (logError) {
    console.error("Error occurred:", error);
  }

  return { message: error.message || defaultMessage };
}
